import React, { useState } from "react"
import dayjs from "dayjs"
import { CalendarBlank, File, CaretDown, CaretUp } from "@phosphor-icons/react"
import { Collapse } from "@blueprintjs/core"
import CustomTag from "components/Atoms/CustomTag"
import {
  InwardAllocationType,
  useEligibleExportCustomersBySampleQuery,
} from "generated/graphql"
import ChemicalTable from "./ChecmicalTable"

function safeValue<T>(
  value: T | null | undefined,
  fallback: string | number = "--"
) {
  if (value === null || value === undefined) return fallback
  if (typeof value === "string" && value.trim() === "") return fallback
  return value
}

export type StackCardSummaryProps = {
  inwardAllocations: InwardAllocationType
  isExpanded?: boolean
}

export default function StackCardSummary({
  inwardAllocations,
  isExpanded = false,
}: StackCardSummaryProps) {
  const [isOpen, setIsOpen] = useState(isExpanded)

  const inward = inwardAllocations?.inward
  const schedule = inward?.booking?.schedule
  const weigh = inward?.weigh
  const farmer = schedule?.plot?.farmer
  const isHoldingAreaAdded: boolean = inwardAllocations?.holdingArea != null

  const chemicalStatus =
    inwardAllocations?.plotCommoditySample?.chemicalStatusDisplay
  const physicalStatus =
    inwardAllocations?.plotCommoditySample?.physicalStatusDisplay

  const isFail = chemicalStatus === "Fail" || physicalStatus === "Fail"
  const isPass = chemicalStatus === "Pass" && physicalStatus === "Pass"

  const { data } = useEligibleExportCustomersBySampleQuery({
    variables: {
      sampleId: inwardAllocations?.plotCommoditySample?.id || "",
    },
    skip: !inwardAllocations?.plotCommoditySample?.id,
  })

  return (
    <div className="p-4 bg-[#F1F8FD] border-2 border-gray-300 rounded-2xl shadow-sm">
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <File size={20} className="text-blue-600" />
            <h5 className="font-bold">Summary</h5>
          </div>
          {!isOpen && (
            <CustomTag minimal intent="primary" className="p-2 rounded-lg ">
              <div className="flex flex-wrap items-center gap-2 text-gray-800">
                <span>
                  <b>Gate Pass</b> {safeValue(schedule?.gatePass)}
                </span>

                <span className="text-gray-400">|</span>
                <span>
                  <b>Variety</b> {safeValue(schedule?.variety?.name)}
                </span>

                {isHoldingAreaAdded && (
                  <>
                    <span className="text-gray-400">|</span>
                    <span>
                      <b>Holding Area</b>{" "}
                      {safeValue(inwardAllocations?.holdingArea?.name)}
                    </span>
                  </>
                )}
              </div>
            </CustomTag>
          )}
        </div>

        <div className="flex items-center gap-3">
          {inward?.date && (
            <CustomTag
              minimal
              intent="primary"
              className="p-2 rounded-lg font-600"
            >
              <div className="flex items-center">
                <CalendarBlank size={16} className="mr-1" />
                Receiving Inward Date: {dayjs(inward.date).format("DD MMM 'YY")}
              </div>
            </CustomTag>
          )}

          {isOpen ? <CaretUp size={20} /> : <CaretDown size={20} />}
        </div>
      </div>

      <Collapse isOpen={isOpen}>
        <div className="mt-8">
          <div className="grid grid-cols-3 gap-y-3 gap-x-8 text-sm">
            <div className="space-y-3">
              <p>
                <b>Gate Pass:</b> <br /> {safeValue(schedule?.gatePass)}
              </p>
              <p>
                <b>MH Code:</b> <br />{" "}
                {safeValue(schedule?.plotCommodity?.registrationCode)}
              </p>
              <p>
                <b>Weight:</b>
                <br /> {safeValue(weigh?.netWeight, 0)} Kg
              </p>
            </div>
            <div className="space-y-3">
              <p>
                <b>Farmer Name:</b> <br />
                {safeValue(farmer?.fullName)}
              </p>
              <p>
                <b>GGN:</b>
                <br /> {safeValue(inwardAllocations?.ggn)}
              </p>
            </div>
            <div className="space-y-3">
              <p>
                <b>Variety:</b>
                <br /> {safeValue(schedule?.variety?.name)}
              </p>
              <p>
                <b>No of Crate received:</b>
                <br /> {safeValue(weigh?.inwardedQuantity)}{" "}
                {safeValue(weigh?.skuType?.name)}
              </p>
            </div>
          </div>

          <hr className="border-dashed border-gray-300" />

          <div>
            <h6 className="font-600 mb-3 underline">Quality Details:</h6>
            <div className="grid grid-cols-3 gap-6 text-sm">
              <div className="space-y-3">
                <p>
                  <b>Pass / Fail:</b> <br />
                  <CustomTag
                    intent={isFail ? "danger" : isPass ? "success" : "warning"}
                    round
                  >
                    {isFail ? "Fail" : isPass ? "Pass" : "-"}
                  </CustomTag>
                </p>
                <p>
                  <b>Individual Higher Value:</b> <br />
                  {safeValue(
                    inwardAllocations?.plotCommoditySample?.maxDetection
                  )}
                </p>
              </div>
              <div className="space-y-3">
                <p>
                  <b>No. of Detections:</b>
                  <br />{" "}
                  {safeValue(
                    inwardAllocations?.plotCommoditySample?.detectionCount
                  )}
                </p>
              </div>
              <div className="space-y-3">
                <p>
                  <b>Total Percentage:</b>
                  <br />{" "}
                  {safeValue(
                    inwardAllocations?.plotCommoditySample?.totalDetection
                  )}
                </p>
              </div>
            </div>
          </div>

          <hr className="border-dashed border-gray-300" />

          {/* Market & Remark */}
          <div>
            <h6 className="font-600 mb-3 underline">Key:</h6>
            <div className="grid grid-cols-[20%_80%] gap-6 text-sm">
              <div>
                <p>
                  <b>Alert</b> <br />
                  {(() => {
                    try {
                      const meta = inward?.metadata
                        ? JSON.parse(inward.metadata)
                        : {}
                      return meta.alert || "--"
                    } catch {
                      return "--"
                    }
                  })()}
                </p>
              </div>
              <div>
                <p className="mb-1">
                  <b>Eligible Customers:</b>
                </p>
                <div className="flex gap-2 flex-wrap">
                  {data?.eligibleExportCustomersBySample?.map((mkt, i) => (
                    <CustomTag key={i} round intent="primary" minimal>
                      {mkt?.name}
                    </CustomTag>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <hr className="border-dashed border-gray-300" />

          {isHoldingAreaAdded && (
            <div>
              <h6 className="font-600 mb-3 underline">Holding Details:</h6>
              <div className="text-sm">
                <p>
                  <b>Holding Area</b> <br />
                  {safeValue(inwardAllocations.holdingArea?.name)}
                </p>
              </div>
            </div>
          )}

          <ChemicalTable inwardAllocations={inwardAllocations} />
        </div>
      </Collapse>
    </div>
  )
}
